/**
 * Tab "Invoice" của /purchase/control — Access `frmProcurementMainSubFormInvoice`.
 * List (frmProcurementQryInvoice → public.v_invoice_p) + toolbar 7 nút (theo Left):
 *   New · Edit · Copy · Accounting - Input Invoices · Tick All · Declaration Date · Delete.
 * (btnPrint "Print BBGN" của Access Visible=False cả 2 nhánh Form_Load → KHÔNG build.)
 * Template dataList chuẩn (/convert-table): FilterBar + Search + per-column ▽ +
 * Columns menu (persist `procurement`/columns/procurement-invoice) + freeze invoice_no
 * + row-select toggle (Edit/Copy/single-Delete) + tick-checkbox cột đầu cho thao tác
 * multi-row (Tick All / Declaration Date).
 */
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import EventNoteIcon from '@mui/icons-material/EventNote'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, type ReactNode, useMemo, useState } from 'react'

import {
  useListInvoicesQuery,
  useBulkDeleteInvoiceMutation,
} from '../../store/api/procurementApi'
import type { InvoiceListItem } from '../../types/api'
import type { ProcurementFilters } from '@/pages/procurement/ProcurementPage'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  useRowMultiSelection,
} from '../../components/dataList'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { titleCaseName } from '../../utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import ProcurementInvoiceFormDialog from './ProcurementInvoiceFormDialog'
import ProcurementInvoiceAccountingDialog from './ProcurementInvoiceAccountingDialog'
import ProcurementInvoiceDeclarationDateDialog from './ProcurementInvoiceDeclarationDateDialog'

interface Props {
  filters: ProcurementFilters
}

type FilterKind = 'search'
type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof InvoiceListItem & string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
  proper?: boolean // title-case VALUE cột tên riêng IN HOA (display-only) — [[feedback_proper_case_cell]] / convert-table §9.3b
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

const FROZEN_KEYS: ReadonlySet<string> = new Set(['invoice_no'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { invoice_no: 140 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { invoice_no: 120 }

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const TICK_COL_WIDTH = 44

// Footer Total — CHỈ sum cột Access `AggregateType=0` (Sum) của frmProcurementQryInvoice
// (TotalsRow=1): quantity, total_amount, vat_amount, total_amount_after_tax. KHÔNG sum
// price_vnd_kg (đơn giá — không có AggregateType). [[convert-table §10.10]]
const SUM_KEYS: ReadonlySet<string> = new Set([
  'quantity',
  'total_amount',
  'vat_amount',
  'total_amount_after_tax',
])

// Thứ tự + caption Default theo danh sách chị Đẹp gửi (2026-07-06) — convert-table §10.8.
// checked_row → cột tick riêng (không trong TABLE_COLUMNS).
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'invoice_id', label: 'Invoice ID', kind: 'int', align: 'right', width: 80 },
  { key: 'alert_txt', label: 'Alert Txt', wrap: true, width: 170 }, // đỏ khi có giá trị
  { key: 'warehouse', label: 'Warehouse', width: 110, proper: true },
  { key: 'invoice_no', label: 'Invoice No', width: 140 }, // frozen
  { key: 'contract_no', label: 'Contract No', width: 130 },
  { key: 'contract_name', label: 'Seller', wrap: true, width: 400, proper: true },
  { key: 'invoice_date', label: 'Invoice Date', kind: 'date', width: 110 },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right', width: 100 },
  { key: 'price_vnd_kg', label: 'Price (VND/KG)', kind: 'number', align: 'right', width: 120 },
  { key: 'quality_code', label: 'Grade', filter: 'checkboxes', width: 90 },
  { key: 'certification', label: 'Certification', filter: 'checkboxes', width: 110 },
  { key: 'license_cert_no', label: 'License Cert No', width: 130 },
  { key: 'total_amount', label: 'Total Amount', kind: 'number', align: 'right', width: 150 },
  { key: 'vat_amount', label: 'VAT Amount', kind: 'number', align: 'right', width: 140 },
  { key: 'total_amount_after_tax', label: 'Invoice Amount (after tax)', kind: 'number', align: 'right', width: 190 },
  { key: 'vat_bank', label: 'VAT Paid Request - Bank', width: 200 },
  { key: 'paid_vat_date', label: 'VAT Paid Request - Date', kind: 'date', width: 170 },
  { key: 'person_update', label: 'Updated Person', width: 210 },
  { key: 'note', label: 'Note', wrap: true, width: 200 },
  { key: 'declaration_date', label: 'Declaration Date', kind: 'date', width: 120 },
  { key: 'receive_geo_date', label: 'Receive Geo Date', kind: 'date', width: 120 },
  { key: 'date_update', label: 'Updated Date', kind: 'datetime', width: 150 },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: InvoiceListItem[], key: keyof InvoiceListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const cellValue = (row: InvoiceListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  // Cột tên riêng IN HOA (contract_name/warehouse) → title-case display-only.
  if (col.proper && typeof val === 'string') return titleCaseName(val)
  // Cột ID (int tự tăng) → số nguyên KHÔNG dấu phân cách nghìn — convert-table §4.
  if (col.kind === 'int') return String(Math.round(Number(val)))
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

export default function ProcurementInvoiceListView({ filters }: Props) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const { canEdit } = usePagePermission()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()

  const [confirm, setConfirm] = useState<{ message: ReactNode; ids: number[] } | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy' } | null>(null)
  const [accountingOpen, setAccountingOpen] = useState(false)
  const [declDateRows, setDeclDateRows] = useState<InvoiceListItem[] | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof InvoiceListItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.invoice.list')

  const filterPopover = useFilterPopover<FilterKind>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof InvoiceListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'procurement',
    prefKey: 'columns',
    subKey: 'procurement-invoice',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'procurement.invoice.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useListInvoicesQuery(
    {
      delivery_latest_from: filters.startDate || undefined,
      delivery_latest_to: filters.endDate || undefined,
      is_forced_close:
        filters.isForcedClose === 'yes' ? true : filters.isForcedClose === 'no' ? false : undefined,
      page: 1,
      page_size: ALL_ROWS_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true, skip: filters.startDate > filters.endDate },
  )

  const [bulkDeleteInvoice, { isLoading: deleteLoading }] = useBulkDeleteInvoiceMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && SUM_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = useMemo(
    () =>
      visibleColumns.reduce((acc, col) => {
        const key = String(col.key)
        const w = getWidth(key) ?? col.width ?? (FROZEN_KEYS.has(key) ? FROZEN_FALLBACK_WIDTH[key] : 100)
        return acc + w
      }, TICK_COL_WIDTH),
    [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof InvoiceListItem) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<InvoiceListItem | null>(
    () => (selectedId == null ? null : rows.find((r) => r.invoice_id === selectedId) ?? null),
    [rows, selectedId],
  )

  // --- Tick helpers (Access checked_row) — hook chung useRowMultiSelection.
  // Tick All / Declaration Date / multi-Delete resolve theo filteredRows.
  const {
    selectedIds: tickedIds,
    setSelectedIds: setTickedIds,
    selectedRows: tickedRows,
    toggle: toggleTick,
    toggleAll: handleTickAll,
    allVisibleSelected: allFilteredTicked,
  } = useRowMultiSelection(filteredRows, (r) => r.invoice_id)
  // Edit/Copy là thao tác 1 dòng (Access btnEdit/btnCopy focus 1 record) → tick
  // >= 2 dòng thì disable, giống rule đã áp ở Pay Data.
  const multiTicked = tickedRows.length > 1
  // Tập tác động cho Declaration Date & Delete: ưu tiên các dòng đã tick; nếu
  // chưa tick gì thì coi dòng đang focus như đã tick 1 dòng.
  const effectiveRows = useMemo<InvoiceListItem[]>(
    () => (tickedRows.length > 0 ? tickedRows : selectedRow ? [selectedRow] : []),
    [tickedRows, selectedRow],
  )


  // Access: nút Edit/Copy/single-Delete mở đầu bằng check Form![subForm]!invoice_no.
  const requireRow = (): InvoiceListItem | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // --- Toolbar handlers ---
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new' })
  }
  const handleEdit = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'edit' })
  }
  const handleCopy = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'copy' })
  }
  const handleAccounting = () => {
    if (!canEdit) return
    setAccountingOpen(true)
  }
  // Access btnAddDate: cần ≥1 row ticked (else "Please choose a record").
  const handleDeclarationDate = () => {
    if (!canEdit) return
    if (effectiveRows.length === 0) {
      snack.warning('Please choose a record')
      return
    }
    setDeclDateRows(effectiveRows)
  }
  const handleDelete = () => {
    if (!canEdit) return
    if (effectiveRows.length === 0) {
      snack.warning('Please choose a record!')
      return
    }
    if (effectiveRows.length > 10) {
      snack.warning('Cannot delete more than 10 records at once. Please tick 10 or fewer.')
      return
    }
    const ids = effectiveRows.map((r) => r.invoice_id)
    const message =
      effectiveRows.length > 1 ? (
        `Do you want to delete ${effectiveRows.length} invoices ?`
      ) : (
        <>
          Do you want to delete
          <br />
          Invoice &apos;{effectiveRows[0].invoice_no ?? ''}&apos; with date at &apos;
          {formatAccessDate(effectiveRows[0].invoice_date)}&apos; ?
        </>
      )
    setConfirm({ message, ids })
  }

  const runConfirm = async () => {
    if (!confirm || confirm.ids.length === 0) {
      setConfirm(null)
      return
    }
    try {
      // Bulk delete (soft-delete BE, atomic 1 call cho cả mảng ids).
      await bulkDeleteInvoice(confirm.ids).unwrap()
      setSelectedId(null)
      setTickedIds(new Set())
      snack.success('Successfully Deleted')
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      setConfirm(null)
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="outlined" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit || multiTicked} onClick={handleEdit} startIcon={<EditIcon sx={{ fontSize: 16 }} />} title={multiTicked ? 'Please choose only one record!' : 'Edit'}>
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit || multiTicked} onClick={handleCopy} startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} title={multiTicked ? 'Please choose only one record!' : 'Copy'}>
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button variant="outlined" size="small" color="secondary" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleAccounting} startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />} title="Accounting - Input Invoices">
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Accounting - Input Invoices</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={effectiveRows.length === 0 || !canEdit} onClick={handleDeclarationDate} startIcon={<EventNoteIcon sx={{ fontSize: 16 }} />} title="Declaration Date">
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Declaration Date</Box>
            </Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={effectiveRows.length === 0 || !canEdit || deleteLoading} onClick={handleDelete} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e: MouseEvent<HTMLElement>) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not load Invoice list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            {tickedRows.length > 0 && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
                Ticked: {tickedRows.length}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Invoice no, contract, warehouse, note…"
              label="Search Invoice"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState tone="error" title="Could not load Invoice list" description="Please refresh the page or contact the system administrator if the issue continues." />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Invoice" description="Try adjusting the Delivery Latest date range or the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              // Access frmProcurementQryInvoice: OrderByOnLoad + GoToRecord acLast
              // → mở màn nhảy tới record cuối (invoice_id lớn nhất, mới nhất),
              // giống Inbound Logistics → Purchase / Pay Data.
              initialTopMostItemIndex={rows.length > 0 ? { index: rows.length - 1, align: 'end' } : 0}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }}
                    sx={{ textAlign: 'center' }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0 }}
                      checked={allFilteredTicked}
                      indeterminate={!allFilteredTicked && tickedRows.length > 0}
                      onChange={handleTickAll}
                      title="Tick all (filtered)"
                    />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = TICK_COL_WIDTH
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-table-header-cell app-sticky-col-2' : 'app-table-header-cell'}
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell padding="checkbox" className="app-sticky-col" style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }} sx={{ bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(isFrozen ? { left: TICK_COL_WIDTH } : {}),
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: InvoiceListItem) => (
                <>
                  <TableCell
                    padding="checkbox"
                    className="app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }}
                    sx={{ textAlign: 'center', py: 0.25 }}
                    onClick={(e) => { e.stopPropagation(); toggleTick(row.invoice_id) }}
                  >
                    <Checkbox size="small" sx={{ p: 0 }} checked={tickedIds.has(row.invoice_id)} onChange={() => toggleTick(row.invoice_id)} onClick={(e) => e.stopPropagation()} />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const val = cellValue(row, col)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const wrap = col.wrap || getWidth(key) != null
                    const frozenStyle: CSSProperties = isFrozen ? { left: TICK_COL_WIDTH } : {}
                    // Access ConditionalFormat: alert_txt tô đỏ khi có giá trị.
                    const isAlert = key === 'alert_txt' && !!val
                    if (wrap) {
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col-2' : undefined}
                          style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top', ...(isAlert ? { color: 'error.main', fontWeight: 600 } : {}) }}
                        >
                          <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top', ...(isAlert ? { color: 'error.main', fontWeight: 600 } : {}) }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
              components={{
                Table: (props) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.invoice_id === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row) toggleSelect(row.invoice_id)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ConfirmDialog
        open={!!confirm}
        title="Delete Invoice"
        message={confirm?.message ?? ''}
        onConfirm={() => void runConfirm()}
        onClose={() => setConfirm(null)}
      />

      {formState && (
        <ProcurementInvoiceFormDialog
          open
          mode={formState.mode}
          invoiceId={formState.mode === 'new' ? null : selectedRow?.invoice_id ?? null}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}

      <ProcurementInvoiceAccountingDialog
        open={accountingOpen}
        canEdit={canEdit}
        onClose={() => setAccountingOpen(false)}
        onSaved={() => setTickedIds(new Set())}
      />

      <ProcurementInvoiceDeclarationDateDialog
        open={declDateRows != null}
        rows={declDateRows ?? []}
        canEdit={canEdit}
        onClose={() => setDeclDateRows(null)}
        onSaved={() => setTickedIds(new Set())}
      />
    </Box>
  )
}
